import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Button,
  DialogActions,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useYupObject } from 'hooks';
import SolicitationSelect, { SolicitationValue } from './SolicitationSelect';
import useNewServiceOrder from './useNewServiceOrder';

function ServiceOrderFormModal({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
}) {
  const yup = useYupObject();
  const { t } = useTranslation(['_common', 'service_order']);

  const validationSchema = yup.object({
    solicitation: yup
      .object({
        id: yup.number().required(),
        name: yup.string().required(),
      })
      .nullable()
      .required(),
    description: yup.string().trim(),
  });

  const initialValues = {
    solicitation: null,
    description: '',
  };

  const { newServiceOrder, loading } = useNewServiceOrder({
    t,
    setOpen,
    refetch,
  });

  useEffect(
    function resetOnOpen() {
      if (open) {
        formik.resetForm();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: ({
      solicitation,
      description,
    }: {
      solicitation: SolicitationValue | null;
      description: string;
    }) => {
      newServiceOrder({ solicitation, description });
    },
  });

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: { width: 750 },
      }}
    >
      <DialogTitle>{t('service_order:new_service_order')}</DialogTitle>
      <Box component="form" noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <DialogContent>
          <SolicitationSelect
            name="solicitation"
            helperText={formik.touched.solicitation && formik.errors.solicitation}
            selected={formik.values.solicitation}
            onChange={(value) => formik.setFieldValue('solicitation', value)}
            error={formik.touched.solicitation && Boolean(formik.errors.solicitation)}
          />

          <TextField
            label={t('service_order:description')}
            name="description"
            id="description"
            autoFocus
            fullWidth
            multiline
            rows={4}
            helperText={formik.touched.description && formik.errors.description}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            autoComplete="off"
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('_common:cancel')}</Button>
          <LoadingButton variant="contained" type="submit" {...{ loading }}>
            {t('_common:save')}
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default ServiceOrderFormModal;
